import { EventRegistry } from 'app/shared/events/event_registry';
import { APIBackedFormField } from 'app/shared/form/APIBackedFormField';
import { isNil } from 'lodash';

export function isTripleEqual(a: any, b: any): boolean {
    return a === b;
}

export function alwaysNull(): null {
    return null;
}

export function passThrough(x: any) {
    return x;
}

/**
 * Returns true if the given argument is not null or undefined
 */
export function hasValue(v: any): boolean {
    return !isNil(v);
}

export class AutoResolvingAPIBackedFormField<T> implements APIBackedFormField<T> {
    private readonly _event: EventRegistry;
    private readonly _isEqual: (a: T, b: T) => boolean;
    private readonly _isValidFn: (v: T) => string | null;
    public readonly displayName: string;
    private readonly _normalizeValue: (a: T) => T;

    private _editingValue: T;
    private _originalValue: T;
    private _savedValue: T;
    private _isValid: boolean;
    private _errorMessage: string | null;

    /**
     * An implementation of the {@link APIBackedFormField} which will auto resolve a conflict
     * if the user has editing to the conflicting value.
     * If the user editing field and new saved value have diverged, the field will enter the conflicted state.
     *
     * @param savedValue The initial value to populate the field with
     * @param fieldDisplayName A display name to use in the view
     * @param isValid A function which can validate a value for this field, returning null means no error,
     *  returning a string will mark as invalid and be used as an error message.
     * @param isEqual A function to check whether two values are equal
     * @param normalizeValue Normalize external values to internal representations, such as nil to an empty string
     */
    constructor(
        savedValue: T,
        fieldDisplayName: string,
        isValid: (v: T) => string | null = alwaysNull,
        isEqual: (a: T, b: T) => boolean = isTripleEqual,
        normalizeValue: (a: T) => T = passThrough,
    ) {
        this._editingValue = normalizeValue(savedValue);
        this._originalValue = this._editingValue;
        this._savedValue = this._originalValue;
        this._isEqual = isEqual;
        this._isValidFn = isValid;
        this._normalizeValue = normalizeValue;
        this.displayName = fieldDisplayName;
        this.validate();
        this._event = new EventRegistry();
    }

    private async notifyListeners() {
        await this._event.notifyListeners();
    }

    private validate(): void {
        this._errorMessage = this._isValidFn(this._editingValue);
        this._isValid = isNil(this._errorMessage);
    }

    isDirty(): boolean {
        return !this._isEqual(this._editingValue, this._originalValue);
    }

    isValid(): boolean {
        return this._isValid;
    }

    isConflicting(): boolean {
        return !this._isEqual(this._savedValue, this._originalValue);
    }

    onChange(fn: () => void): () => void {
        // we're not using a cached event registry because our event is called with no args,
        //  so there's nothing to cache. But we still can't want to call each listener immediately
        fn();
        return this._event.addEventListener(fn);
    }

    async setValue(value: T): Promise<void> {
        const editingValue = this._normalizeValue(value);
        if (!this._isEqual(editingValue, this._editingValue)) {
            this._editingValue = editingValue;
            this.validate();
            await this.notifyListeners();
        }
    }

    getValue(): T {
        return this._editingValue;
    }

    getSavedValue(): T {
        return this._savedValue;
    }

    async setSavedValue(value: T): Promise<void> {
        const normalizedValue = this._normalizeValue(value);
        const oldSavedValue = this._savedValue;
        const oldOriginalValue = this._originalValue;

        // update our saved value
        this._savedValue = normalizedValue;

        // if our new saved value is actually equal to our currently editing value;
        // we can silently update our original value as well, making it no longer dirty
        if (this._isEqual(this._savedValue, this._editingValue)) {
            this._originalValue = this._savedValue;
        }

        // if our editing value is equal to our original value (not dirty), then we can
        //  silently update our original editing and original value too, making it no longer dirty.
        if (!this.isDirty()) {
            this._originalValue = this._savedValue;
            this._editingValue = this._savedValue;
            this.validate();
        }

        // check if we actually changed something before emitting a change event
        if (
            !this._isEqual(this._savedValue, oldSavedValue) ||
            !this._isEqual(this._originalValue, oldOriginalValue)
        ) {
            await this.notifyListeners();
        }
    }

    async applyConflict(): Promise<void> {
        if (!this.isConflicting()) {
            return;
        }
        this._originalValue = this._savedValue;
        this._editingValue = this._savedValue;
        this.validate();
        await this.notifyListeners();
    }

    async cancelConflict(): Promise<void> {
        if (!this.isConflicting()) {
            return;
        }
        this._originalValue = this._savedValue;
        await this.notifyListeners();
    }

    getErrorMessage(): string | null {
        return this._errorMessage;
    }

    destroy(): any {
        this._event.destroy();
    }
}
